import { t, Trans } from '@lingui/macro';
import { Player } from "@lottiefiles/react-lottie-player";
import { AlertCircle, ArrowLeft, XCircle, Users } from 'lucide-react';
import { useEffect, useState } from "react";
import { useHistory, useParams } from "../../hooks/useRouterCompat";
import { Workspace, canJoinAsMember, getInvitationPolicy } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import ProfileImg from "../components/ProfileImg";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "../../components/ui/card";
import { Avatar } from "../../components/ui/avatar";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";
import { useToast } from "../../components/ui/use-toast";

const WorkspaceInvitationPage: React.FC = () => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace>();
  const [isMobileBrowser, setIsMobileBrowser] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const { toast } = useToast();
  const { invitationKey } = useParams<{ invitationKey: string }>();

  useEffect(() => {
    const platform = UtilApp.getPlatform();
    setIsMobileBrowser(platform === 'android-browser' || platform === 'ios-browser');
    start();
  }, [invitationKey])

  async function start() {
    setInProgress(true);
    let r = await Fire.ws.getWorkspaceByInvitationKey({ invitationKey: invitationKey || "" });
    console.log("Workspace invite response:", r);

    setWorkspace(r.workspace ?? undefined);
    if (r.status === 'NOT_FOUND') {
      // toast({
      //   variant: "destructive",
      //   title: t`Workspace not found`,
      //   description: t`Workspace cannot be found.`
      // });
    }
    setInProgress(false);
  }

  async function joinWorkspace() {
    if (!workspace)
      return;

    setInProgress(true);
    try {
      const res = await Fire.ws.joinWorkspace({ workspaceId: workspace.id, invitationKey: invitationKey || null });
      if (res === 'JOINED') {
        toast({ description: t`You have joined the workspace ${workspace.name}` });
        history.replace(`/ws/${workspace.id}`);
      }
      else if (res === 'NOT_ALLOWED') {
        toast({ variant: "destructive", description: t`You are not allowed to join ${workspace.name}` });
      }
      else if (res === 'ALREADY_MEMBER') {
        toast({ description: t`You are already a member of ${workspace.name}` });
        history.replace(`/ws/${workspace.id}`); // Redirect if already member
      } else {
        toast({ variant: "destructive", description: t`Unknown error, could not join workspace.` });
      }
    } catch (error) {
      toast({ variant: "destructive", description: `Error: ${(error as Error).message}` });
    }
    setInProgress(false);
  }

  return (
    <div className="h-full bg-muted/50 p-4 flex flex-col items-center justify-center">

      {/* Header for Back Navigation */}
      {My.isLoggedIn() && (
        <div className="absolute top-4 left-4">
          <Button variant="ghost" size="icon" onClick={() => history.goBack()}>
            <ArrowLeft className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* Loading State */}
      {!workspace && inProgress && (
        <div className="text-center mt-20">
          <p className="text-muted-foreground mb-4"><Trans>Fetching the invitation...</Trans></p>
          <Player autoplay keepLastFrame src="assets/splash.json" style={{ height: '160px', width: '160px' }} />
        </div>
      )}

      {/* Invitation Content */}
      {workspace && (
        <Card className="w-full max-w-md mx-auto shadow-lg border-none">
          <CardHeader className="text-center pb-2">
            <p className="text-sm font-medium text-primary mb-1 leading-none uppercase tracking-wider"><Trans>Workspace Invitation</Trans></p>
            <p className="text-lg font-medium text-foreground border-b pb-4 mx-4 border-border"><Trans>Invitation link by {workspace.owner.name}</Trans></p>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 pt-4">

            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Users className="w-5 h-5 text-muted-foreground" />
                <h2 className="text-2xl font-bold text-foreground">{workspace.name}</h2>
              </div>
              {workspace.description && (
                <div
                  dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(workspace.description, My.user.id) }}
                  className="text-card-foreground text-sm"
                />
              )}
            </div>

            <div className="bg-muted/50 rounded-xl p-4 w-full text-center space-y-3 border border-border">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider"><Trans>Workspace owner</Trans></span>
              <div className="flex flex-col items-center gap-2">
                <Avatar className="w-16 h-16 ring-2 ring-white shadow-sm">
                  <ProfileImg url={workspace.owner.photoUrl} className="object-cover" />
                </Avatar>
                <span className="font-bold text-foreground">{workspace.owner.name}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                <Trans>{workspace.name} was created {UtilApp.dateStrToYmd(workspace.created, navigator.language)} and is managed by {workspace.owner.name}</Trans>
              </p>
            </div>

            <div className="w-full pt-4">
              {getActionButton()}
            </div>

            {getPromotionText()}
          </CardContent>
        </Card>
      )}

      {/* Error/Expired States */}
      {(!inProgress && !workspace) && (
        <div className="text-center space-y-6 max-w-sm mx-auto mt-12 bg-background p-8 rounded-xl shadow-sm">
          {isExpired ? (
            <div className="flex flex-col items-center gap-4">
              <XCircle className="w-16 h-16 text-destructive" />
              <h1 className="text-xl font-bold text-foreground"><Trans>Link Expired</Trans></h1>
              <p className="text-card-foreground"><Trans>This invitation link has expired.</Trans></p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="w-16 h-16 text-muted-foreground" />
              <h4 className="text-xl font-medium text-foreground"><Trans>Invitation cannot be found.</Trans></h4>
              <Button variant="outline" onClick={() => history.push('/')}>
                <Trans>Back</Trans>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  function getActionButton() {
    if (My.isLoggedIn())
      return getJoinButton();
    else {
      if (isMobileBrowser) {
        const mainDomain = import.meta.env.VITE_MAIN_DOMAIN || 'app.nextpod.jp';
        const urlScheme = mainDomain.startsWith('devapp') ? 'nextpod-dev' : 'nextpod';
        let openWithAppLink = `${urlScheme}://${mainDomain}/ws-invite/${invitationKey}`;
        return (
          <div className="text-center space-y-4 p-4 bg-blue-50 rounded-xl">
            <h6 className="font-semibold text-blue-900"><Trans>You're on a mobile browser.</Trans></h6>
            <div className="space-y-2">
              <p className="text-sm text-blue-800"><Trans>Install the mobile app</Trans></p>
              <div className="flex justify-center gap-2">
                <a href="https://play.google.com/store/apps/details?id=jp.nextpod.app" target="_blank" rel="noopener noreferrer">
                  <img src="/assets/img/badge-google.png" className="h-9" alt="Get it on Google Play" />
                </a>
                <a href="https://apps.apple.com/app/nextpod/id6752560289" target="_blank" rel="noopener noreferrer">
                  <img src="/assets/img/badge-apple.png" className="h-9" alt="Download on the App Store" />
                </a>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm text-blue-800 mb-2"><Trans>Already installed?</Trans></p>
              <a href={openWithAppLink} className="inline-block px-4 py-2 bg-blue-600 text-on-primary-text rounded-full text-sm font-medium hover:bg-blue-700 transition-colors">
                <Trans>Open with NEXTPOD app</Trans>
              </a>
            </div>

            <p className="text-xs text-blue-600 pt-2"><Trans>You can also use NEXTPOD on desktop at https://app.nextpod.jp</Trans></p>
          </div>
        );
      } else {
        return getLoginButton()
      }
    }
  }

  function getJoinButton() {
    if (workspace) {
      // 초대 링크 경유 = 유효한 초대 키 보유 (이 페이지는 키 조회로만 도달 가능)
      // 도메인 제한 워크스페이스는 링크가 있어도 허용 도메인이어야 가입 가능
      const canJoin = canJoinAsMember(workspace, My.user?.email, true);

      return (
        <div className="space-y-3 w-full">
          {/* 정책 정보 표시 */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs text-muted-foreground border border-border">
            {getInvitationPolicy(workspace) === 'ALLOW_LISTED_EMAIL_DOMAIN' && workspace.emailDomains?.length > 0 && (
              <div className="flex items-start gap-1.5">
                <span className="font-medium text-card-foreground shrink-0"><Trans>Member:</Trans></span>
                <span>{workspace.emailDomains.join(', ')}</span>
              </div>
            )}
            {My.isLoggedIn() && (
              <div className="pt-1 border-t border-border">
                {canJoin ? (
                  <p className="text-green-600"><Trans>You will join as a Member.</Trans></p>
                ) : (
                  <p className="text-destructive"><Trans>Your email domain is not allowed to join this workspace.</Trans></p>
                )}
              </div>
            )}
          </div>

          <Button disabled={inProgress || !canJoin} onClick={joinWorkspace} className="w-full bg-primary hover:bg-primary/90 text-on-primary-text h-12 text-lg font-bold">
            <Trans>Join the workspace</Trans>
          </Button>
        </div>
      );
    }
    return null;
  }

  function getLoginButton() {
    let path = `/ws-invite/${invitationKey}`;
    return (
      <div className="space-y-3 w-full">
        <p className="text-sm text-center text-card-foreground"><Trans>You need to sign up/login to join the workspace.</Trans></p>
        <Button
          onClick={() => history.push(`/login?redirect=${encodeURIComponent(path)}`)}
          data-cy="signup"
          variant="outline"
          className="w-full h-12 font-semibold"
        >
          <Trans>Sign up/login</Trans>
        </Button>
      </div>
    );
  }

  function getPromotionText() {
    if (!My.isLoggedIn()) {
      return (
        <div className="w-full">
          <Separator className="my-6" />
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <img src="assets/icon/favicon.png" alt="Logo" className="w-10 h-10 rounded-lg shadow-sm" />
            </div>
            <h5 className="font-semibold text-foreground leading-tight">
              <Trans>Easy communication with NEXTPOD<br /> No matter where you are.</Trans>
            </h5>
            <p className="text-sm text-card-foreground leading-relaxed text-justify px-2">
              <Trans>NEXTPOD is a cloud based communication service that is designed to simplify remote collaboration between
                teams across and within organizations. You can create private channels and invite people for collaboration
                using instant messaging, voice and screen recordings, file sharing and real time voice calls.</Trans>
            </p>
          </div>
        </div>
      );
    }
    return null;
  }
}


export default WorkspaceInvitationPage;